import React, {useState, Fragment, ReactNode, useCallback} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import b_ from 'b_';

import I18N from 'assets/i18n/brandlift-project';

import {CreativeCurrency, QuicksurveyStore, SurveyPreset} from 'client/common/types';
import {Device} from 'client/common/utils/device-width';
import {tld} from 'client/common/utils/tld';
import {getCreativeCurrency} from 'client/common/utils/price';
import {isWithinDates} from 'client/common/utils/date';

import {useDevice} from 'client/common/hooks/device';

import {Button, Link} from 'client/common/components/lego';
import {getInitialItems} from 'client/common/store/reducers/question-bank/helpers';
import {isSimpleTemplate} from 'client/common/store/reducers/question-bank/types';
import {deselectPreset as deselectPresetAction} from 'client/common/store/reducers/question-bank';
import {getPreset} from 'client/common/store/selectors/question-bank';

import UXHeatMapImage from './images/ux-heat-map.jpg';
import UXFlowImage from './images/ux-flow.svg';

import BackIcon from './icons/back.svg';
import Description from './description';
import EmptyState from './empty-state';
import InitialItems from './initial-items';

import {PlainDescriptionProps, PresetDescriptionProps} from './types';

import './preset-description.css';
import {useUser} from 'client/common/contexts/user';

const i18n = I18N.surveyCreate.presetDescription;
const b = b_.with('preset-description');

const HELP_REQUIRED_PRESETS = [SurveyPreset.CreateForMe, SurveyPreset.Crowd];

/* eslint-disable react/display-name, react/prop-types */
export const DESCRIPTION_CONTENTS: {
    [key in SurveyPreset]?: (params: {creativeCurrency: CreativeCurrency}) => ReactNode;
} = {
    [SurveyPreset.UX]: () => (
        <Fragment>
            <p>{i18n.presets[SurveyPreset.UX].content.heatMapResult}</p>
            <img src={UXHeatMapImage} style={{maxWidth: 240}} />
            <Link
                href={`https://surveys.yandex.${tld}/ux/statistics/9W3HGkTnJtg4Wgsb56X7Ko/public`}
                rel='noopener noreferrer'
                target='_blank'
                view='default'
            >
                {i18n.presets.ux.content.statisticsPreview}
            </Link>
            <p>{i18n.presets.ux.content.heatMapColorTip}</p>
            <h3>{i18n.presets.ux.content.howToStart}</h3>
            <p>{i18n.presets.ux.content.step1}</p>
            <img src={UXFlowImage} />
            <p>{i18n.presets.ux.content.step2}</p>
            <p>{i18n.presets.ux.content.step3}</p>
            <p>{i18n.presets.ux.content.step4}</p>
            <p>{i18n.presets.ux.content.step5}</p>
            <p>{i18n.presets.ux.content.step6}</p>
            <Link
                href='/help/ux-test/goals.html'
                rel='noopener noreferrer'
                target='_blank'
                view='default'
            >
                {i18n.presets.ux.content.manual}
            </Link>
        </Fragment>
    ),
    [SurveyPreset.Creative]: ({creativeCurrency}) => (
        <Fragment>
            <p>{i18n.presets[SurveyPreset.Creative].content.text1}</p>
            <p>{i18n.presets[SurveyPreset.Creative].content.text2}</p>
            <p>{i18n.presets[SurveyPreset.Creative].content.result}</p>
            <p>{i18n.presets[SurveyPreset.Creative].content.costs[creativeCurrency]}</p>
            <Link
                href='https://surveys.yandex.ru/help/creative/creative-about.html'
                rel='noopener noreferrer'
                target='_blank'
                view='default'
            >
                {i18n.creativeDescription}
            </Link>
        </Fragment>
    ),
    [SurveyPreset.Bordomatik]: ({creativeCurrency}) => (
        <Fragment>
            <p>{i18n.presets[SurveyPreset.Bordomatik].content.text1}</p>
            <p>{i18n.presets[SurveyPreset.Bordomatik].content.text2}</p>
            <p>{i18n.presets[SurveyPreset.Bordomatik].content.result}</p>
            <p>{i18n.presets[SurveyPreset.Bordomatik].content.costs[creativeCurrency]}</p>
            <Link
                href='https://surveys.yandex.ru/help/bordomatik/bordomatik-about.html'
                rel='noopener noreferrer'
                target='_blank'
                view='default'
            >
                {i18n.creativeDescription}
            </Link>
        </Fragment>
    ),
    [SurveyPreset.BannerTest]: () => (
        <Fragment>
            <p>{i18n.presets[SurveyPreset.BannerTest].text1}</p>
            <p>{i18n.presets[SurveyPreset.BannerTest].text2}</p>
            <p>{i18n.presets[SurveyPreset.BannerTest].result}</p>
            <p>{i18n.presets[SurveyPreset.BannerTest].cost}</p>
        </Fragment>
    ),
    [SurveyPreset.YandexAdScriptTest]: () => (
        <Link
            href='/landings/methodology/box-yndx-scenariotest'
            rel='noopener noreferrer'
            target='_blank'
            view='default'
        >
            {i18n.description}
        </Link>
    ),
    [SurveyPreset.YandexAdStoryboardTest]: () => (
        <Link
            href='/landings/methodology/box-yndx-imagetest'
            rel='noopener noreferrer'
            target='_blank'
            view='default'
        >
            {i18n.description}
        </Link>
    ),
    [SurveyPreset.YandexAdVideoTest]: () => (
        <Link
            href='/landings/methodology/box-yndx-videotest'
            rel='noopener noreferrer'
            target='_blank'
            view='default'
        >
            {i18n.description}
        </Link>
    ),
    [SurveyPreset.YandexNameTest]: () => (
        <Link
            href='/landings/methodology/box-yndx-naming'
            rel='noopener noreferrer'
            target='_blank'
            view='default'
        >
            {i18n.description}
        </Link>
    ),
    [SurveyPreset.YandexConceptTest]: () => (
        <Link
            href='/landings/methodology/box-yndx-concept'
            rel='noopener noreferrer'
            target='_blank'
            view='default'
        >
            {i18n.description}
        </Link>
    ),
    [SurveyPreset.YandexInterfaceMindfulness]: () => (
        <Link
            href='/landings/methodology/box-yndx-mindfulness'
            rel='noopener noreferrer'
            target='_blank'
            view='default'
        >
            {i18n.description}
        </Link>
    ),
    [SurveyPreset.YandexSerpSurvey]: () => (
        <Fragment>
            <Link
                href={I18N.surveyEditor.learnMoreLink.yandexSerpSurvey}
                rel='noopener noreferrer'
                target='_blank'
                view='default'
            >
                {i18n.presets.serpSurvey}
            </Link>
        </Fragment>
    ),
    [SurveyPreset.YandexActiveFeedback]: () => <Fragment />,
    [SurveyPreset.YandexPassiveFeedback]: () => <Fragment />
};
/* eslint-enable */

function PlainDescription(props: PlainDescriptionProps) {
    const {preset, userInfo} = props;

    const renderContent = DESCRIPTION_CONTENTS[preset];

    return (
        <Description>
            {renderContent &&
                renderContent({
                    creativeCurrency: getCreativeCurrency(userInfo)
                })}
        </Description>
    );
}

// eslint-disable-next-line max-statements
export default function PresetDescription(props: PresetDescriptionProps) {
    const {submitLabel = i18n.moveToEditing, onSubmit} = props;

    const dispatch = useDispatch();
    const preset = useSelector((state: QuicksurveyStore) => getPreset(state));
    const deselectPreset = useCallback(() => dispatch(deselectPresetAction()), [dispatch]);

    const [loading, setLoading] = useState(false);
    const [accountId] = useState<number | undefined>(undefined);
    const user = useUser();

    const handleHideItems = () => {
        deselectPreset();
    };

    const device = useDevice();

    if (!preset) {
        return device === Device.Mobile ? null : (
            <div className={b()}>
                <EmptyState />
            </div>
        );
    }

    const handleSubmit = () => {
        const isLoading = onSubmit({preset, accountId});

        setLoading(isLoading);
    };

    return (
        <div className={b()}>
            <div className={b('background')} onClick={handleHideItems} />
            <div className={b('overlay')}>
                <div className={b('header')}>
                    <img
                        className={b('icon', {type: 'back'})}
                        src={BackIcon}
                        onClick={deselectPreset}
                    />
                    <h2 className={b('preset-name')}>{i18n.presets[preset].name}</h2>
                </div>
                <span className={b('preset-description')} dangerouslySetInnerHTML={{__html: i18n.presets[preset].description}}/>
                <div className={b('buttons')}>
                    <Button
                        className={b('create-survey-button')}
                        onClick={handleSubmit}
                        size='m'
                        progress={loading ? true : undefined}
                        view='action'
                    >
                        {submitLabel}
                    </Button>
                    {/* isYandexPublisher && !isAgency && (
                        <PaymentAccountSelect onAccountChange={handleAccountChange} />
                    ) */}
                </div>
                {HELP_REQUIRED_PRESETS.includes(preset) &&
                    isWithinDates({to: new Date(2024, 0, 9, 10)}) && (
                        <div className={b('preset-warning')}>
                            {I18N.common.header2.warnings.newYear}
                        </div>
                    )}
                <div className={b('overlay-gradient')} />
            </div>
            {isSimpleTemplate(preset) ? (
                <InitialItems items={getInitialItems(preset)} />
            ) : (
                <PlainDescription preset={preset} userInfo={user?.info} />
            )}
        </div>
    );
}
